/**
 * Filter Panel Component
 * 
 * This component provides a sidebar with filter controls for refining search results.
 * Filters include:
 * - No Website checkbox
 * - Has Website checkbox
 * - Minimum rating slider
 * - Minimum reviews input
 * - Has Phone checkbox
 * 
 * It also displays statistics about the current results.
 */

import React, { useState, useEffect } from 'react';

function FilterPanel({ onApplyFilters, onClearFilters, isLoading, statistics }) {
  // ═══════════════════════════════════════════════════════════════
  // STATE
  // ═══════════════════════════════════════════════════════════════
  
  // Filter values
  const [filters, setFilters] = useState({
    no_website: false,
    has_website: false,
    min_rating: null,
    min_reviews: null,
    has_phone: false,
  });

  // ═══════════════════════════════════════════════════════════════
  // HANDLERS
  // ═══════════════════════════════════════════════════════════════

  /**
   * Handle checkbox changes
   */
  const handleCheckboxChange = (field) => {
    setFilters(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  /**
   * Handle rating slider change
   */
  const handleRatingChange = (value) => {
    setFilters(prev => ({
      ...prev,
      min_rating: value === 0 ? null : value
    }));
  };

  /**
   * Handle reviews input change
   */
  const handleReviewsChange = (value) => {
    const numValue = parseInt(value) || 0;
    setFilters(prev => ({
      ...prev,
      min_reviews: numValue === 0 ? null : numValue
    }));
  };

  /**
   * Handle apply filters button click
   */
  const handleApply = () => {
    // Remove null/undefined values before sending
    const activeFilters = Object.fromEntries(
      Object.entries(filters).filter(([_, v]) => v !== null && v !== false)
    );
    onApplyFilters(activeFilters);
  };

  /**
   * Handle clear filters button click
   */
  const handleClear = () => {
    setFilters({
      no_website: false,
      has_website: false,
      min_rating: null,
      min_reviews: null,
      has_phone: false,
    });
    onClearFilters();
  };

  // ═══════════════════════════════════════════════════════════════
  // RENDER HELPERS
  // ═══════════════════════════════════════════════════════════════

  /**
   * Check if any filters are active
   */
  const hasActiveFilters = () => {
    return Object.values(filters).some(v => v !== null && v !== false);
  };

  // ═══════════════════════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════════════════════

  return (
    <div className="card p-5 sticky top-24">
      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <h3 className="text-lg font-semibold text-white flex items-center">
          <svg className="w-5 h-5 mr-2 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
          </svg>
          Filters
        </h3>
        {hasActiveFilters() && (
          <span className="text-xs bg-blue-500/20 text-blue-400 px-2 py-1 rounded-full">
            Active
          </span>
        )}
      </div>

      {/* Filter Controls */}
      <div className="space-y-5">
        
        {/* Website Filters */}
        <div>
          <label className="block text-sm font-medium text-gray-400 mb-2">
            Website Status
          </label>
          <div className="space-y-2">
            <label className="flex items-center space-x-3 p-2 rounded-lg hover:bg-slate-800/50 cursor-pointer transition-colors">
              <input
                type="checkbox"
                checked={filters.no_website}
                onChange={() => handleCheckboxChange('no_website')}
                disabled={isLoading}
              />
              <span className="text-gray-300 text-sm">No Website</span>
            </label>
            <label className="flex items-center space-x-3 p-2 rounded-lg hover:bg-slate-800/50 cursor-pointer transition-colors">
              <input
                type="checkbox"
                checked={filters.has_website}
                onChange={() => handleCheckboxChange('has_website')}
                disabled={isLoading}
              />
              <span className="text-gray-300 text-sm">Has Website</span>
            </label>
          </div>
        </div>

        {/* Divider */}
        <div className="border-t border-white/10" />

        {/* Rating Filter */}
        <div>
          <label className="block text-sm font-medium text-gray-400 mb-2">
            Minimum Rating
          </label>
          <div className="space-y-3">
            <input
              type="range"
              min="0"
              max="5"
              step="0.5"
              value={filters.min_rating || 0}
              onChange={(e) => handleRatingChange(parseFloat(e.target.value))}
              className="w-full"
              disabled={isLoading}
            />
            <div className="flex justify-between items-center">
              <span className="text-xs text-gray-500">Any</span>
              <span className="text-sm font-medium text-yellow-400">
                {filters.min_rating ? `${filters.min_rating} ⭐` : 'Any'}
              </span>
              <span className="text-xs text-gray-500">5.0</span>
            </div>
          </div>
        </div>

        {/* Divider */}
        <div className="border-t border-white/10" />

        {/* Reviews Filter */}
        <div>
          <label className="block text-sm font-medium text-gray-400 mb-2">
            Minimum Reviews
          </label>
          <input
            type="number"
            min="0"
            placeholder="e.g., 10"
            value={filters.min_reviews || ''}
            onChange={(e) => handleReviewsChange(e.target.value)}
            className="form-input text-sm"
            disabled={isLoading}
          />
        </div>

        {/* Divider */}
        <div className="border-t border-white/10" />

        {/* Phone Filter */}
        <div>
          <label className="block text-sm font-medium text-gray-400 mb-2">
            Contact Info
          </label>
          <label className="flex items-center space-x-3 p-2 rounded-lg hover:bg-slate-800/50 cursor-pointer transition-colors">
            <input
              type="checkbox"
              checked={filters.has_phone}
              onChange={() => handleCheckboxChange('has_phone')}
              disabled={isLoading}
            />
            <span className="text-gray-300 text-sm">Has Phone Number</span>
          </label>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="mt-6 space-y-2">
        <button
          onClick={handleApply}
          disabled={isLoading}
          className="btn-primary w-full py-2.5 text-sm flex items-center justify-center space-x-2 disabled:opacity-50"
        >
          {isLoading ? (
            <>
              <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              <span>Applying...</span>
            </>
          ) : (
            <>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
              </svg>
              <span>Apply Filters</span>
            </>
          )}
        </button>
        
        {hasActiveFilters() && (
          <button
            onClick={handleClear}
            disabled={isLoading}
            className="btn-secondary w-full py-2.5 text-sm"
          >
            Clear All
          </button>
        )}
      </div>

      {/* Statistics */}
      {statistics && (
        <div className="mt-6 pt-5 border-t border-white/10">
          <h4 className="text-sm font-medium text-gray-400 mb-3">Statistics</h4>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-500">Total Leads</span>
              <span className="text-white font-medium">{statistics.total || 0}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">With Phone</span>
              <span className="text-green-400">{statistics.with_phone || 0}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">With Website</span>
              <span className="text-blue-400">{statistics.with_website || 0}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">No Website</span>
              <span className="text-purple-400">{statistics.without_website || 0}</span>
            </div>
            {statistics.avg_rating && (
              <div className="flex justify-between">
                <span className="text-gray-500">Avg Rating</span>
                <span className="text-yellow-400">{statistics.avg_rating} ⭐</span>
              </div>
            )}
            {statistics.avg_reviews && (
              <div className="flex justify-between">
                <span className="text-gray-500">Avg Reviews</span>
                <span className="text-white">{statistics.avg_reviews}</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Tips */}
      <div className="mt-5 p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg">
        <p className="text-xs text-blue-300">
          <span className="font-semibold">Tip:</span> Businesses without websites 
          are often easier to contact and more likely to need web development services.
        </p>
      </div>
    </div>
  );
}

export default FilterPanel;
